<template>
  <RygenButton
    class="edit-quote-margin-apply-all-button"
    :label="t('rateShop.charges.applyMarginToAll')"
    mode="tertiary"
    icon="pi pi-chevron-down"
    icon-pos="right"
    aria-haspopup="dialog"
    :aria-expanded="isPopoverVisible"
    @click="togglePopover"
  />

  <Popover
    ref="popover"
    class="edit-quote-margin-apply-all-select-popover"
    @show="isPopoverVisible = true"
    @hide="isPopoverVisible = false"
  >
    <div class="edit-quote-margin-apply-all-select-popover-header">
      <span class="margin-apply-all-title">
        {{ t('rateShop.charges.applyMarginToAllTitle') }}
      </span>
      <span class="margin-apply-all-subtitle">
        {{ t('rateShop.charges.applyMarginToAllSubtitle') }}
      </span>
    </div>

    <div class="edit-quote-margin-apply-all-select-popover-body">
      <div
        v-for="charge in selectableCharges"
        :key="charge.key"
        class="apply-charge-radio-button-container"
      >
        <RadioButton
          v-model="selection"
          :input-id="`apply-all-${charge.key}`"
          :name="RADIO_BUTTON_NAME_KEY"
          :value="charge.key"
          variant="filled"
        />
        <label
          :for="`apply-all-${charge.key}`"
          class="flex justify-content-between align-items-center w-full"
        >
          <span class="apply-charge-name-label">
            {{
              t('rateShop.charges.useChargeParam', {
                charge: charge.chargeCode?.name,
              })
            }}
          </span>
          <span class="apply-charge-margin-label">
            {{ formatChargeMargin(charge) }}
          </span>
        </label>
      </div>

      <div class="apply-charge-radio-button-container">
        <RadioButton
          v-model="selection"
          :input-id="NEW_MARGIN_KEY"
          :name="RADIO_BUTTON_NAME_KEY"
          :value="NEW_MARGIN_KEY"
          variant="filled"
        />
        <label :for="NEW_MARGIN_KEY">
          {{ t('rateShop.charges.setNewMargin') }}
        </label>
      </div>

      <EditQuoteChargeUpliftRateAndUnitInputGroup
        v-if="isSetNewMargin"
        :id="NEW_MARGIN_KEY"
        v-model="newMargin.rate"
        v-model:unit="newMargin.unit"
        class="edit-quote-margin-apply-new-margin"
        :currency="currencyCode"
        append-to="self"
      />
    </div>

    <div class="edit-quote-margin-apply-all-select-popover-footer">
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton mode="primary" :label="t('common.apply')" @click="onApply" />
    </div>
  </Popover>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderMoney } from '@rygen/rygen-ui/i18n';
import { Currency } from '@rygen/rygen-ui/model';

import { RateQuoteChargeUpliftRuleUnit } from '@/model';
import {
  BaseEditQuoteChargeTableItem,
  EditQuoteThreeplChargeTableItem,
} from '@/model/rate/editQuote';

import EditQuoteChargeUpliftRateAndUnitInputGroup from '@/components/rateShop/editQuote/EditQuoteChargeUpliftRateAndUnitInputGroup.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Popover from 'primevue/popover';
import RadioButton from 'primevue/radiobutton';

type MarginRateAndUnit = {
  rate: number;
  unit: RateQuoteChargeUpliftRuleUnit;
};

const NEW_MARGIN_KEY = 'apply-all-new-margin';
const RADIO_BUTTON_NAME_KEY = 'apply-margin-to-all';

const props = defineProps({
  chargeItems: {
    type: Array as PropType<BaseEditQuoteChargeTableItem[]>,
    default: () => [],
    required: true,
  },
  currencyCode: {
    type: String as PropType<Currency>,
    default: () => Currency.USD,
  },
});
const { chargeItems, currencyCode } = toRefs(props);

const { t } = useI18n();

const popover: Ref<InstanceType<typeof Popover> | null> = ref(null);
const selection: Ref<string> = ref(NEW_MARGIN_KEY);
const newMargin: Ref<MarginRateAndUnit> = ref({
  rate: 0,
  unit: RateQuoteChargeUpliftRuleUnit.FLAT,
});
const isPopoverVisible = ref(false);

const currencyUnit = computed(() => ({ currencyCode: currencyCode.value }));

const isSetNewMargin = computed(() => selection.value === NEW_MARGIN_KEY);

/**
 * Only 3PL charges with a selected charge code can act as a margin source or
 * target — accessorials that haven't been assigned a code yet are skipped.
 */
const selectableCharges = computed((): EditQuoteThreeplChargeTableItem[] =>
  chargeItems.value.filter(
    (charge): charge is EditQuoteThreeplChargeTableItem =>
      charge instanceof EditQuoteThreeplChargeTableItem &&
      charge.hasChargeSelection,
  ),
);

const togglePopover = (event: Event): void => {
  // Clicking the trigger while open closes it. PrimeVue's outside-click dismiss
  // ignores clicks on the trigger, so this handler is the sole authority here.
  if (isPopoverVisible.value) {
    popover.value?.hide();
    return;
  }

  // Reset to a clean "set a new margin", as default, state each time the popover opens.
  selection.value = NEW_MARGIN_KEY;
  newMargin.value = { rate: 0, unit: RateQuoteChargeUpliftRuleUnit.FLAT };

  // Capture the clicked button now — event.currentTarget is nulled out once
  // dispatch finishes, so it would be null inside the async nextTick callback.
  const trigger = event.currentTarget as HTMLElement | null;

  popover.value?.show(event);

  nextTick(() => {
    // Use this instance's own overlay element, not a global selector, so
    // multiple EditQuoteChargesSection instances on a page don't cross-wire.
    // `container` is a runtime property on the Popover instance not surfaced
    // in its public type, so we cast to reach it.
    const popoverEl = (
      popover.value as unknown as { container?: HTMLElement } | null
    )?.container;

    if (trigger && popoverEl) {
      popoverEl.style.left = `${trigger.getBoundingClientRect().left}px`;
    }
  });
};

const onCancel = (): void => {
  popover.value?.hide();
};

const onApply = (): void => {
  if (isSetNewMargin.value) {
    applyToAll(newMargin.value);
  } else {
    const source = selectableCharges.value.find(
      (charge) => charge.key === selection.value,
    );

    if (source) {
      applyToAll({ rate: source.marginRate, unit: source.upliftUnit });
    }
  }

  popover.value?.hide();
};

const applyToAll = (margin: MarginRateAndUnit): void => {
  for (const charge of selectableCharges.value) {
    charge.marginRate = margin.rate;
    charge.upliftUnit = margin.unit;
    charge.sellRate = charge.calculateSellRate();
  }
};

const formatChargeMargin = (
  charge: EditQuoteThreeplChargeTableItem,
): string => {
  if (charge.upliftUnit === RateQuoteChargeUpliftRuleUnit.PERCENT) {
    return `${charge.marginRate}%`;
  }

  return renderMoney(charge.marginRate, currencyUnit.value);
};
</script>

<style>
.edit-quote-margin-apply-all-button {
  .pi-chevron-down {
    transition: transform 0.15s ease;
  }

  &[aria-expanded='true'] .pi-chevron-down {
    transform: rotate(180deg);
  }
}

.edit-quote-margin-apply-all-select-popover {
  .p-popover-content {
    display: flex;
    flex-direction: column;
    gap: var(--gap-2);
  }

  .edit-quote-margin-apply-all-select-popover-header {
    display: flex;
    flex-direction: column;

    .margin-apply-all-title {
      font-weight: 600;
    }

    .margin-apply-all-subtitle {
      font-size: 0.875rem;
      color: var(--p-gray-400);
    }
  }

  .edit-quote-margin-apply-all-select-popover-body {
    display: flex;
    flex-direction: column;
    gap: var(--gap-2);

    .apply-charge-radio-button-container {
      display: flex;
      gap: var(--gap-1);
      align-items: center;
    }

    label {
      cursor: pointer;
    }

    .apply-charge-margin-label {
      /* Adds space between labels when name is long - simpler than handling overflow */
      margin-left: var(--sp-3);
      color: var(--p-gray-400);
    }
  }

  .edit-quote-margin-apply-all-select-popover-footer {
    display: flex;
    gap: var(--gap-1);
    align-items: center;
    justify-content: end;
  }
}
</style>
